import { useNavigation } from "hooks/navigate";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import { useI18n, type Language } from "providers/I18nProvider";
import { usePreferredLanguage } from "providers/PreferredLanguageProvider";
import { useCallback, useEffect, useRef } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";

export const LanguageLayout = () => {
  const { language } = useParams<{ language?: string }>();
  const location = useLocation();
  const navigate = useNavigation();
  const hasRedirectedRef = useRef(false);
  const { authState } = useAuth();
  const { preferredLanguage, loading: preferredLanguageLoading } =
    usePreferredLanguage();
  const { languages, languageKeys, setLanguage } = useI18n();

  const isLanguage = useCallback(
    (value?: string): value is Language => !!value && value in languages,
    [languages],
  );

  useEffect(() => {
    if (isLanguage(language)) {
      setLanguage(language);
    }
  }, [language, setLanguage]);

  useEffect(() => {
    if (isLanguage(language)) {
      hasRedirectedRef.current = false;
      return;
    }

    if (hasRedirectedRef.current) {
      return;
    }

    if (authState.status === AuthStatus.Pending) {
      return;
    }

    if (
      authState.status === AuthStatus.Authenticated &&
      preferredLanguageLoading
    ) {
      return;
    }

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];
    const hasLanguagePrefix = isLanguage(firstSegment);
    const pathWithoutLang = hasLanguagePrefix
      ? "/" + pathSegments.slice(1).join("/")
      : location.pathname;

    let targetLanguage: Language = languageKeys[0];
    if (
      authState.status === AuthStatus.Authenticated &&
      preferredLanguage &&
      isLanguage(preferredLanguage)
    ) {
      targetLanguage = preferredLanguage;
    }

    const targetPath = `/${targetLanguage}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;

    hasRedirectedRef.current = true;
    setLanguage(targetLanguage);
    navigate(targetPath, {
      replace: true,
      preserveSearchParams: true,
      state: location.state,
    });
  }, [
    language,
    navigate,
    location.pathname,
    location.state,
    authState.status,
    preferredLanguage,
    preferredLanguageLoading,
    setLanguage,
  ]);

  if (!isLanguage(language)) {
    return null;
  }

  return <Outlet />;
};
